import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Grid, Chip, Stack, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TableSortLabel,
  Select, MenuItem, FormControl, InputLabel, CircularProgress, Alert, Tooltip, IconButton,
  Card, CardContent,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Repeat as RepeatIcon,
  PriorityHigh as PriorityIcon,
  Schedule as ScheduleIcon,
  Engineering as EngineeringIcon,
  AttachMoney as MoneyIcon,
  HealthAndSafety as HealthIcon,
} from '@mui/icons-material';
import { getAftercareOperations } from '../services/api';

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmtNum = (n) => (n == null ? '—' : new Intl.NumberFormat('en-US').format(Number(n)));
const fmtCurrency = (n) =>
  n == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const fmt1 = (n) => (n == null ? '—' : Number(n).toFixed(1));

const STATUS_LABEL = { 0: 'New', 6: 'Scheduled' };
const STATUS_COLOR = { 0: 'info', 6: 'primary' };

const PRIORITY_LABEL = { 0: '—', 10: 'Low', 20: 'Normal', 30: 'High', 50: 'Emergency' };
const priorityColor = (p) => (p === 50 ? 'error' : p === 30 ? 'warning' : 'default');

// Highlight matched follow-up keywords inside a note excerpt.
function highlightFollowupTerms(text, keywords) {
  if (!text) return null;
  const lower = text.toLowerCase();
  const hits = (keywords || [])
    .map(kw => ({ kw, idx: lower.indexOf(kw.toLowerCase()) }))
    .filter(h => h.idx >= 0)
    .sort((a, b) => a.idx - b.idx);
  if (!hits.length) return <span>{text}</span>;
  // Walk through text once, splicing highlights
  const out = [];
  let cursor = 0;
  for (const h of hits) {
    if (h.idx < cursor) continue; // overlap
    if (h.idx > cursor) out.push(<span key={`p${cursor}`}>{text.slice(cursor, h.idx)}</span>);
    out.push(
      <mark key={`m${h.idx}`} style={{ background: '#fff3cd', padding: '0 2px', borderRadius: 2, fontWeight: 600 }}>
        {text.slice(h.idx, h.idx + h.kw.length)}
      </mark>
    );
    cursor = h.idx + h.kw.length;
  }
  if (cursor < text.length) out.push(<span key={`p${cursor}`}>{text.slice(cursor)}</span>);
  return <>{out}</>;
}

// ─── Sort hook ────────────────────────────────────────────────────────────────
function useSort(initial) {
  const [sort, setSort] = useState(initial);
  const handle = (col) => {
    setSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  };
  return [sort, handle];
}
function applySort(rows, sort) {
  if (!rows || !sort?.col) return rows || [];
  const dir = sort.dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = a[sort.col]; const bv = b[sort.col];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

// ─── Headline KPI card ────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, color = 'primary.main' }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ color, mb: 0.5 }}>
          {icon}
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AftercareOperationsView({ onOpenClient = () => {} }) {
  const [windowDays, setWindowDays] = useState(90);

  // Helper: row click handler. Tables that have an ar_code per row use this to
  // deep-link into the Clients tab drilldown.
  const rowProps = (arcode) => ({
    onClick: () => arcode && onOpenClient(arcode),
    sx: { cursor: arcode ? 'pointer' : 'default', '&:hover': arcode ? { bgcolor: 'action.hover' } : {} },
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async (days = windowDays) => {
    setLoading(true); setError(null);
    try {
      const r = await getAftercareOperations(days);
      setData(r.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(windowDays); /* eslint-disable-next-line */ }, [windowDays]);

  // Sort hooks for each table
  const [followupSort, setFollowupSort] = useSort({ col: 'PRIORITY', dir: 'desc' });
  const [stuckSort, setStuckSort] = useSort({ col: 'age_days', dir: 'desc' });
  const [repeatsSort, setRepeatsSort] = useSort({ col: 'visit_count', dir: 'desc' });
  const [sameWeekSort, setSameWeekSort] = useSort({ col: 'same_week_pairs', dir: 'desc' });
  const [stressSort, setStressSort] = useSort({ col: 'open_now', dir: 'desc' });
  const [techSort, setTechSort] = useSort({ col: 'open_count', dir: 'desc' });
  const [problemsSort, setProblemsSort] = useSort({ col: 'occurrences', dir: 'desc' });
  const [nteSort, setNteSort] = useSort({ col: 'age_days', dir: 'desc' });

  // Memoized sorted slices
  const followupsSorted = useMemo(() => applySort(data?.followups, followupSort), [data, followupSort]);
  const stuckSorted = useMemo(() => applySort(data?.stuck, stuckSort), [data, stuckSort]);
  const repeatsSorted = useMemo(() => applySort(data?.repeats, repeatsSort), [data, repeatsSort]);
  const sameWeekSorted = useMemo(() => applySort(data?.same_week_repeats, sameWeekSort), [data, sameWeekSort]);
  const stressSorted = useMemo(() => applySort(data?.clients_in_stress, stressSort), [data, stressSort]);
  const techSorted = useMemo(() => applySort(data?.tech_load, techSort), [data, techSort]);
  const problemsSorted = useMemo(() => applySort(data?.top_problems, problemsSort), [data, problemsSort]);
  const nteSorted = useMemo(() => applySort(data?.nte_breaches, nteSort), [data, nteSort]);

  const sortHeader = (sort, setSort, col, label, align = 'left') => (
    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }} align={align}>
      <TableSortLabel
        active={sort.col === col}
        direction={sort.col === col ? sort.dir : 'asc'}
        onClick={() => setSort(col)}
      >{label}</TableSortLabel>
    </TableCell>
  );

  if (loading && !data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!data) return null;

  const { headline, daily_trend, coverage, followup_keywords } = data;
  const coveragePct = coverage?.total > 0 ? Math.round((coverage.covered / coverage.total) * 100) : 0;
  const billablePct = 100 - coveragePct;

  // Daily trend bars (simple inline sparkline)
  const maxTrend = Math.max(1, ...(daily_trend || []).flatMap(d => [d.new_count, d.closed_count]));

  return (
    <Box>
      {/* Toolbar */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Showing data for the last {windowDays} days. Status 2 (Cancelled) is excluded throughout.
            “Open” = STATUS New (0) + Scheduled (6). <strong>Click any row to see the full client history.</strong>
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Window</InputLabel>
          <Select value={windowDays} label="Window" onChange={(e) => setWindowDays(Number(e.target.value))}>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
            <MenuItem value={180}>Last 180 days</MenuItem>
            <MenuItem value={365}>Last 365 days</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Refresh"><span><IconButton onClick={() => fetchData(windowDays)} disabled={loading}><RefreshIcon /></IconButton></span></Tooltip>
      </Stack>

      {/* ─── Headline KPIs ─── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            icon={<PriorityIcon fontSize="small" />}
            label="Open WOs"
            value={fmtNum(headline.total_open)}
            sub={`${fmtNum(headline.new_count)} new · ${fmtNum(headline.scheduled_count)} scheduled`}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            icon={<WarningIcon fontSize="small" />}
            label="High / Emergency"
            value={fmtNum(headline.high_priority)}
            sub={headline.emergency > 0 ? `🚨 ${headline.emergency} emergency` : 'no emergencies'}
            color={headline.emergency > 0 ? 'error.main' : 'warning.main'}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            icon={<ScheduleIcon fontSize="small" />}
            label="Avg Age (days)"
            value={fmt1(headline.avg_age_days)}
            sub={`oldest: ${fmtNum(headline.max_age_days)}d`}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            icon={<EngineeringIcon fontSize="small" />}
            label="No Tech Assigned"
            value={fmtNum(headline.no_tech_assigned)}
            sub={headline.no_tech_assigned > 0 ? 'awaiting dispatch' : 'all assigned'}
            color={headline.no_tech_assigned > 0 ? 'warning.main' : 'success.main'}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            icon={<HealthIcon fontSize="small" />}
            label="Unique Clients"
            value={fmtNum(headline.unique_clients)}
            sub={`${fmtNum(headline.unique_techs)} techs working`}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            icon={<MoneyIcon fontSize="small" />}
            label={`Revenue (${windowDays}d)`}
            value={fmtCurrency(coverage?.total_revenue || 0)}
            sub={`${billablePct}% billable · ${coveragePct}% covered`}
          />
        </Grid>
      </Grid>

      {/* ─── Daily trend ─── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <TrendingUpIcon fontSize="small" sx={{ color: '#0D2C54' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Last 14 days — new vs closed</Typography>
        </Stack>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 80 }}>
          {(daily_trend || []).map((d, i) => (
            <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: 60, gap: 0.25 }}>
                <Tooltip title={`New: ${d.new_count}`}>
                  <Box sx={{ width: 10, height: `${(d.new_count / maxTrend) * 60}px`, bgcolor: '#0D2C54', borderRadius: 0.5 }} />
                </Tooltip>
                <Tooltip title={`Closed: ${d.closed_count}`}>
                  <Box sx={{ width: 10, height: `${(d.closed_count / maxTrend) * 60}px`, bgcolor: '#4caf50', borderRadius: 0.5 }} />
                </Tooltip>
              </Box>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                {new Date(d.day).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
              </Typography>
            </Box>
          ))}
        </Box>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Chip size="small" label="New" sx={{ bgcolor: '#0D2C54', color: 'white' }} />
          <Chip size="small" label="Closed" sx={{ bgcolor: '#4caf50', color: 'white' }} />
        </Stack>
      </Paper>

      {/* ─── Tech-flagged follow-ups ─── */}
      <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: '#fff3cd', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningIcon sx={{ color: '#856404' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#856404' }}>
              Needs Follow-Up — flagged by tech notes
            </Typography>
            <Chip size="small" sx={{ bgcolor: '#ffc107' }} label={(followupsSorted || []).length} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Open WOs whose tech notes contain phrases like “follow up”, “waiting on”, “left voicemail”, “still need”, “return visit”, etc.
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
              {sortHeader(followupSort, setFollowupSort, 'WRKORDNBR', 'WO#')}
              {sortHeader(followupSort, setFollowupSort, 'client_name', 'Client')}
              {sortHeader(followupSort, setFollowupSort, 'PRIORITY', 'Priority')}
              {sortHeader(followupSort, setFollowupSort, 'STATUS', 'Status')}
              {sortHeader(followupSort, setFollowupSort, 'age_days', 'Age', 'right')}
              {sortHeader(followupSort, setFollowupSort, 'tech_name', 'Tech')}
              <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Note Excerpt</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {(followupsSorted || []).length === 0 ? (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  No tech-flagged follow-ups in this window.
                </TableCell></TableRow>
              ) : (followupsSorted || []).map(f => (
                <TableRow key={f.WRKORDNBR} hover {...rowProps(f.ar_code)}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{f.WRKORDNBR}</TableCell>
                  <TableCell><Typography variant="body2" fontWeight={600}>{f.client_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{f.ar_code}</Typography></TableCell>
                  <TableCell><Chip size="small" color={priorityColor(f.PRIORITY)} label={PRIORITY_LABEL[f.PRIORITY] || '—'} /></TableCell>
                  <TableCell><Chip size="small" color={STATUS_COLOR[f.STATUS] || 'default'} label={STATUS_LABEL[f.STATUS] || f.STATUS} /></TableCell>
                  <TableCell align="right">{f.age_days}d</TableCell>
                  <TableCell><Typography variant="caption">{f.tech_name || '—'}</Typography></TableCell>
                  <TableCell sx={{ maxWidth: 480 }}>
                    <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.4 }}>
                      {highlightFollowupTerms(f.notes_excerpt, followup_keywords)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ─── Stuck (open ≥7 days) + Same-week repeats — side by side ─── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ overflow: 'hidden', height: '100%' }}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Stuck — open ≥7 days <Chip size="small" sx={{ ml: 1 }} label={(stuckSorted || []).length} />
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {sortHeader(stuckSort, setStuckSort, 'WRKORDNBR', 'WO#')}
                  {sortHeader(stuckSort, setStuckSort, 'client_name', 'Client')}
                  {sortHeader(stuckSort, setStuckSort, 'tech_name', 'Tech')}
                  {sortHeader(stuckSort, setStuckSort, 'age_days', 'Age', 'right')}
                </TableRow></TableHead>
                <TableBody>
                  {(stuckSorted || []).slice(0, 60).map(s => (
                    <TableRow key={s.WRKORDNBR} hover {...rowProps(s.ar_code)}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{s.WRKORDNBR}</TableCell>
                      <TableCell><Typography variant="body2">{s.client_name}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{s.tech_name || '—'}</Typography></TableCell>
                      <TableCell align="right">
                        <Chip size="small" color={s.age_days >= 30 ? 'error' : s.age_days >= 14 ? 'warning' : 'default'} label={`${s.age_days}d`} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ overflow: 'hidden', height: '100%' }}>
            <Box sx={{ p: 2, bgcolor: '#fdecea', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <RepeatIcon fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Same-week repeats</Typography>
                <Chip size="small" sx={{ bgcolor: '#f44336', color: 'white' }} label={(sameWeekSorted || []).length} />
              </Stack>
              <Typography variant="caption" color="text.secondary">Sites visited 2+ times within a 7-day window — strong signal something didn't get fixed.</Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {sortHeader(sameWeekSort, setSameWeekSort, 'client_name', 'Client')}
                  {sortHeader(sameWeekSort, setSameWeekSort, 'same_week_pairs', '# Pairs', 'right')}
                  {sortHeader(sameWeekSort, setSameWeekSort, 'min_gap_days', 'Tightest', 'right')}
                  {sortHeader(sameWeekSort, setSameWeekSort, 'most_recent_repeat', 'Most Recent', 'right')}
                </TableRow></TableHead>
                <TableBody>
                  {(sameWeekSorted || []).map(r => (
                    <TableRow key={r.ar_code} hover {...rowProps(r.ar_code)}>
                      <TableCell><Typography variant="body2" fontWeight={600}>{r.client_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.ar_code}</Typography></TableCell>
                      <TableCell align="right"><Chip size="small" color="error" label={r.same_week_pairs} /></TableCell>
                      <TableCell align="right">{r.min_gap_days}d</TableCell>
                      <TableCell align="right"><Typography variant="caption">{fmtDate(r.most_recent_repeat)}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* ─── Repeat-visit watch ─── */}
      <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Top sites by visit count — last {windowDays} days <Chip size="small" sx={{ ml: 1 }} label={(repeatsSorted || []).length} />
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
              {sortHeader(repeatsSort, setRepeatsSort, 'client_name', 'Client')}
              {sortHeader(repeatsSort, setRepeatsSort, 'visit_count', 'Visits', 'right')}
              {sortHeader(repeatsSort, setRepeatsSort, 'unique_techs', 'Techs', 'right')}
              {sortHeader(repeatsSort, setRepeatsSort, 'open_now', 'Open Now', 'right')}
              {sortHeader(repeatsSort, setRepeatsSort, 'total_billed', 'Billed', 'right')}
              {sortHeader(repeatsSort, setRepeatsSort, 'last_visit', 'Last Visit', 'right')}
            </TableRow></TableHead>
            <TableBody>
              {(repeatsSorted || []).map(r => (
                <TableRow key={r.ar_code} hover {...rowProps(r.ar_code)}>
                  <TableCell><Typography variant="body2" fontWeight={600}>{r.client_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.ar_code}</Typography></TableCell>
                  <TableCell align="right"><Chip size="small" color="primary" label={r.visit_count} /></TableCell>
                  <TableCell align="right">{r.unique_techs}</TableCell>
                  <TableCell align="right">{r.open_now > 0 ? <Chip size="small" color="warning" label={r.open_now} /> : '—'}</TableCell>
                  <TableCell align="right">{fmtCurrency(r.total_billed)}</TableCell>
                  <TableCell align="right"><Typography variant="caption">{fmtDate(r.last_visit)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ─── Clients in stress ─── */}
      <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: '#fef3c7', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Clients in stress — high visit count + currently open <Chip size="small" sx={{ ml: 1 }} label={(stressSorted || []).length} />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ≥4 visits in window AND ≥1 currently open. Relationship pressure regardless of revenue.
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
              {sortHeader(stressSort, setStressSort, 'client_name', 'Client')}
              {sortHeader(stressSort, setStressSort, 'visits_in_window', 'Visits', 'right')}
              {sortHeader(stressSort, setStressSort, 'open_now', 'Open Now', 'right')}
              {sortHeader(stressSort, setStressSort, 'unique_techs', 'Techs', 'right')}
              {sortHeader(stressSort, setStressSort, 'total_billed', 'Billed', 'right')}
              {sortHeader(stressSort, setStressSort, 'last_visit', 'Last Visit', 'right')}
            </TableRow></TableHead>
            <TableBody>
              {(stressSorted || []).map(c => (
                <TableRow key={c.ar_code} hover {...rowProps(c.ar_code)}>
                  <TableCell><Typography variant="body2" fontWeight={600}>{c.client_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.ar_code}</Typography></TableCell>
                  <TableCell align="right">{c.visits_in_window}</TableCell>
                  <TableCell align="right"><Chip size="small" color="error" label={c.open_now} /></TableCell>
                  <TableCell align="right">{c.unique_techs}</TableCell>
                  <TableCell align="right">{fmtCurrency(c.total_billed)}</TableCell>
                  <TableCell align="right"><Typography variant="caption">{fmtDate(c.last_visit)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ─── Tech load + Top problems — side by side ─── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ overflow: 'hidden', height: '100%' }}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Tech load — open WOs per tech</Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {sortHeader(techSort, setTechSort, 'tech_name', 'Tech')}
                  {sortHeader(techSort, setTechSort, 'open_count', 'Open', 'right')}
                  {sortHeader(techSort, setTechSort, 'high_priority', 'High Pri', 'right')}
                  {sortHeader(techSort, setTechSort, 'avg_age_days', 'Avg Age', 'right')}
                  {sortHeader(techSort, setTechSort, 'max_age_days', 'Oldest', 'right')}
                </TableRow></TableHead>
                <TableBody>
                  {(techSorted || []).map(t => (
                    <TableRow key={t.tech_id} hover>
                      <TableCell><Typography variant="body2">{t.tech_name || `#${t.tech_id}`}</Typography></TableCell>
                      <TableCell align="right"><Chip size="small" color="primary" label={t.open_count} /></TableCell>
                      <TableCell align="right">{t.high_priority > 0 ? <Chip size="small" color="warning" label={t.high_priority} /> : '—'}</TableCell>
                      <TableCell align="right">{fmt1(t.avg_age_days)}d</TableCell>
                      <TableCell align="right">{t.max_age_days}d</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ overflow: 'hidden', height: '100%' }}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Top problems — last {windowDays} days</Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {sortHeader(problemsSort, setProblemsSort, 'problem', 'Problem')}
                  {sortHeader(problemsSort, setProblemsSort, 'occurrences', 'Count', 'right')}
                  {sortHeader(problemsSort, setProblemsSort, 'unique_clients', 'Clients', 'right')}
                  {sortHeader(problemsSort, setProblemsSort, 'avg_hours', 'Avg Hrs', 'right')}
                  {sortHeader(problemsSort, setProblemsSort, 'total_billed', 'Billed', 'right')}
                </TableRow></TableHead>
                <TableBody>
                  {(problemsSorted || []).map((p, i) => (
                    <TableRow key={i} hover>
                      <TableCell><Typography variant="body2">{p.problem || '—'}</Typography></TableCell>
                      <TableCell align="right"><Chip size="small" label={p.occurrences} /></TableCell>
                      <TableCell align="right">{p.unique_clients}</TableCell>
                      <TableCell align="right">{fmt1(p.avg_hours)}</TableCell>
                      <TableCell align="right">{fmtCurrency(p.total_billed)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* ─── NTE breaches ─── */}
      {(nteSorted || []).length > 0 && (
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: '#ffebee', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'error.main' }}>
              NTE Breach Watch — actuals at ≥80% of Not-To-Exceed <Chip size="small" sx={{ ml: 1 }} color="error" label={nteSorted.length} />
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                {sortHeader(nteSort, setNteSort, 'WRKORDNBR', 'WO#')}
                {sortHeader(nteSort, setNteSort, 'client_name', 'Client')}
                {sortHeader(nteSort, setNteSort, 'NOTTOEXCEEDV', 'NTE', 'right')}
                {sortHeader(nteSort, setNteSort, 'actual_cost', 'Actual', 'right')}
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>% of NTE</TableCell>
                {sortHeader(nteSort, setNteSort, 'age_days', 'Age', 'right')}
              </TableRow></TableHead>
              <TableBody>
                {(nteSorted || []).map(n => {
                  const pct = n.NOTTOEXCEEDV > 0 ? (n.actual_cost / n.NOTTOEXCEEDV) * 100 : 0;
                  return (
                    <TableRow key={n.WRKORDNBR} hover {...rowProps(n.ar_code)}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{n.WRKORDNBR}</TableCell>
                      <TableCell>{n.client_name}</TableCell>
                      <TableCell align="right">{fmtCurrency(n.NOTTOEXCEEDV)}</TableCell>
                      <TableCell align="right">{fmtCurrency(n.actual_cost)}</TableCell>
                      <TableCell align="right"><Chip size="small" color={pct >= 100 ? 'error' : pct >= 95 ? 'warning' : 'default'} label={`${pct.toFixed(0)}%`} /></TableCell>
                      <TableCell align="right">{n.age_days}d</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Generated {fmtDate(data.generated_at)}. Sage SM is read-only — closing WOs still requires action in Sage directly.
      </Typography>
    </Box>
  );
}
